import { useState, useEffect, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTestimonials } from "../hooks/useHomepageContent";
import { cn } from "@/lib/utils/cn";

export default function Testimonials() {
  const { data: testimonials, isLoading } = useTestimonials();
  const [activeIdx, setActiveIdx] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);

  const total = testimonials?.length ?? 0;

  const go = useCallback(
    (dir: 1 | -1) => {
      setDirection(dir);
      setActiveIdx((i) => (i + dir + total) % total);
    },
    [total],
  );

  // Auto-advance every 6s
  useEffect(() => {
    if (total <= 1) return;
    const timer = setInterval(() => go(1), 6000);
    return () => clearInterval(timer);
  }, [go, total]);

  if (isLoading) return null;
  if (!total) return null;

  const active = testimonials![activeIdx];

  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 60 : -60,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -60 : 60,
      opacity: 0,
      transition: { duration: 0.25, ease: "easeIn" },
    }),
  };

  return (
    <section className="relative overflow-hidden bg-metro-cream py-16 md:py-24">
      {/* Dot grid background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.035]"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(circle, #0d4a2c 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative mx-auto max-w-5xl px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10 text-center md:mb-14">
          <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-metro-green-100">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="text-metro-green-800"
            >
              <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
            </svg>
          </div>
          <h2 className="font-serif text-display-md text-metro-green-950">
            Happy Guests
          </h2>
        </div>

        {/* Carousel wrapper */}
        <div className="relative flex items-center gap-2 md:gap-4">
          {/* Prev button */}
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous testimonial"
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition md:h-11 md:w-11",
              "hover:border-metro-green-800 hover:text-metro-green-800",
              total <= 1 && "invisible",
            )}
          >
            <ChevronLeft size={16} className="md:hidden" />
            <ChevronLeft size={18} className="hidden md:block" />
          </button>

          {/* Card */}
          <div className="relative min-w-0 flex-1 overflow-hidden">
            <AnimatePresence custom={direction} mode="wait">
              <motion.div
                key={activeIdx}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                <div className="rounded-2xl bg-white px-6 py-8 shadow-card md:px-10 md:py-10 lg:px-12 lg:py-12">
                  {/* Stars */}
                  <div className="mb-4 flex justify-center gap-1 md:mb-5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        className={cn(
                          i < active.rating
                            ? "fill-metro-gold text-metro-gold"
                            : "fill-gray-100 text-gray-100",
                          "md:w-4 md:h-4",
                        )}
                      />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="mb-6 text-center font-serif text-base leading-relaxed text-metro-green-950 md:mb-8 md:text-lg lg:text-xl">
                    &ldquo;{active.quote}&rdquo;
                  </blockquote>

                  {/* Divider */}
                  <div className="mb-5 flex items-center gap-4 md:mb-6">
                    <div className="h-px flex-1 bg-metro-cream-100" />
                    <div className="h-1 w-1 rounded-full bg-metro-gold" />
                    <div className="h-px flex-1 bg-metro-cream-100" />
                  </div>

                  {/* Author */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-metro-green-800 font-serif text-base text-white shadow-sm ring-2 ring-metro-green-100 md:h-11 md:w-11">
                      {active.name[0]}
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-metro-green-950">
                        {active.name}
                      </p>
                      <p className="text-sm text-metro-ink-subtle">
                        {active.role}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Next button */}
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next testimonial"
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition md:h-11 md:w-11",
              "hover:border-metro-green-800 hover:text-metro-green-800",
              total <= 1 && "invisible",
            )}
          >
            <ChevronRight size={16} className="md:hidden" />
            <ChevronRight size={18} className="hidden md:block" />
          </button>
        </div>

        {/* Dot pagination */}
        {total > 1 && (
          <div className="mt-6 flex justify-center gap-2 md:mt-8">
            {testimonials!.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  setDirection(idx > activeIdx ? 1 : -1);
                  setActiveIdx(idx);
                }}
                aria-label={`Go to testimonial ${idx + 1}`}
                className={cn(
                  "rounded-full transition-all duration-300",
                  idx === activeIdx
                    ? "h-2.5 w-8 bg-metro-green-800"
                    : "h-2.5 w-2.5 bg-metro-green-800/30 hover:bg-metro-green-800/60",
                )}
              />
            ))}
          </div>
        )}

        {/* Counter */}
        {total > 1 && (
          <p className="mt-3 text-center text-xs text-metro-ink-subtle">
            {activeIdx + 1} / {total}
          </p>
        )}
      </div>
    </section>
  );
}